import React, { CSSProperties, useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";


const defaultStyles: CSSProperties = {
    display: 'none'
};

type Props = {
    children: React.ReactNode;
    className?: string;
    position?: 'right' | 'center';
    childrenFixed?: boolean;
};

const PopUpController: React.FC<Props> = ({ children, className, position, childrenFixed }) => {
    const [dropStyles, setDropStyles] = useState<CSSProperties>(defaultStyles);
    const [status, setStatus] = useState(false);
    //** Children */
    const elements = React.Children.toArray(children).filter(React.isValidElement);
    const buttonElement = elements.find((c: React.ReactElement<any>) => c.props['data-popup-role'] === "button");
    const dropElement = elements.find((c: React.ReactElement<any>) => c.props['data-popup-role'] === "drop");
    const wrapperRef = useRef<HTMLDivElement>(null);
    const location = useLocation();

    const injectCloseCb = (element: React.ReactElement) => {
        if(!element?.props?.children)
            return element;
        const children = React.Children.toArray(element.props.children);
        const injectedChildren = children.map((child) => {
            if (React.isValidElement(child)) {
                if (!child.props["data-popup-action"])
                    return React.cloneElement(child);

                let _actions: Record<string, any> = {};
                if (child.props["data-popup-action"].includes("close"))
                    _actions.closePopup = setOff;
                if (child.props["data-popup-action"].includes("status"))
                    _actions.status = status;
                return React.cloneElement(child, _actions);
            }
            return child;
        });
        return React.cloneElement(element, { children: injectedChildren });
    };

    const handleStatus = useCallback((value: boolean) => {
        setDropStyles((prev) => (
            value ? { ...prev, display: 'block' }: { ...prev, display:'none' }
        ));
        setStatus(value);
    }, []);

    const setOff = useCallback(() => {
        const menu = wrapperRef.current?.querySelector<HTMLElement>('[data-popup-role="drop"]'); 
        const button = wrapperRef.current?.querySelector<HTMLElement>('[data-popup-role="button"]');
        if(!button || !menu) 
            return;
        handleStatus(false);
        // setDropStyles((prev) => ({ ...prev, display:'none' }));
    }, []);

    useEffect(() => {
        setOff();
    }, [location, setOff]);


    useEffect(() => {
        const menu = wrapperRef.current?.querySelector<HTMLElement>('[data-popup-role="drop"]'); 
        const button = wrapperRef.current?.querySelector<HTMLElement>('[data-popup-role="button"]');
        if(!button || !menu) 
            return;

        const handleClickOutside = (e: MouseEvent) => {
            if(!wrapperRef.current)
                return;
            if (wrapperRef.current.contains(e.target as Node)) return;
            if(!!menu && !menu.contains(e.target as Node))
                handleStatus(false);
        };

        const updatePosition = () => {
            if(!button || !wrapperRef.current) 
                return; 
            const isNested = wrapperRef.current.closest('[data-popup-role="drop"]');
            if (isNested) {
                setDropStyles(prev => ({
                    ...prev,
                    position: childrenFixed ? 'fixed': 'absolute',
                    top: button.offsetTop,
                    left: button.offsetWidth,
                    transform: `translateX(-100%)`
                }));
                return;
            }
            const wrapperWidth = wrapperRef.current?.offsetWidth;
            const rect = button.getBoundingClientRect();
            const viewWidth = window.innerWidth;
            const marginRight = 20;
            const _position = position ?? 'right';
            let leftPosition = 0;
            if(_position === "right") {
                leftPosition = Math.max(rect.right - wrapperWidth, 0); 
                if(leftPosition + wrapperWidth + marginRight > viewWidth)
                    leftPosition = viewWidth - wrapperWidth - marginRight;
                if(leftPosition < 0)
                    leftPosition = 0;
            }
            setDropStyles((prev) => ({ 
                ...prev, 
                position: 'fixed',
                top: rect.bottom,
                left: leftPosition,
                transform: `translateX(calc(-100% + ${Math.round(button.offsetWidth)}px))` 
            }));
        };

        // const status = (value: boolean) => {
        //     setDropStyles((prev) => (
        //         value ? { ...prev, display: 'block' }: { ...prev, display:'none' }
        //     ));
        // };

        const toggle = () => {
            handleStatus(!status);
            updatePosition();
            // setDropStyles((prev) => {
            //     if(prev.display === 'none')
            //         return { ...prev, display: 'block' };
            //     return { ...prev, display:'none' };
            // });
        };
        
        updatePosition();
        button.addEventListener('click', toggle);
        window.addEventListener('scroll', updatePosition);
        window.addEventListener('resize', updatePosition);
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            button.removeEventListener('click', toggle);
            window.removeEventListener('scroll', updatePosition);
            window.removeEventListener('resize', updatePosition);
            document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [status]);

    return (
        <div ref={wrapperRef} className={className} style={{position: 'relative'}}>
            {buttonElement}
            {(    
                <div className="topbar-menu" style={dropStyles}>
                    {injectCloseCb(dropElement as React.ReactElement)}
                </div>
            )}
        </div>
    );
};

export default PopUpController;